import { AnimatePresence, motion } from 'motion/react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { CircularLoader } from '@blade/ui-kit'

import { CheckCircleIcon, CrossCircleIcon } from '../icons'

import type { IconProps } from '../icons'
import type { ClassNameProps } from '@root/shared/react'

export type Status = 'loading' | 'error' | 'success'
type StatusBoxProps = {
  status: AsyncStateN.AsyncStateN<unknown, unknown> | Status
  icon?: React.FC<IconProps>
} & ClassNameProps

export const StatusBox: React.FC<StatusBoxProps> = ({
  className,
  status,
  icon,
}) => {
  const resolvedStatus = useMemo((): Status => {
    if (typeof status === 'string') {
      return status
    }
    if (AsyncStateN.isSuccess(status)) {
      return 'success'
    }
    if (AsyncStateN.isError(status)) {
      return 'error'
    }
    return 'loading'
  }, [status])

  const renderIcon = () => {
    const Icon = icon
    if (Icon) {
      return <Icon size={32} />
    }
    if (resolvedStatus === 'loading') {
      return <CircularLoader size={32} />
    }
    if (resolvedStatus === 'success') {
      return <SuccessIcon size={32} />
    }
    return <ErrorIcon size={32} />
  }
  return (
    <Root className={className}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          key={resolvedStatus}
        >
          {renderIcon()}
        </motion.div>
      </AnimatePresence>
    </Root>
  )
}

const SuccessIcon = styled(CheckCircleIcon)`
  color: var(--success-9);
`
const ErrorIcon = styled(CrossCircleIcon)`
  color: var(--red-9);
`

const Root = styled.div`
  width: 60px;
  height: 60px;

  display: grid;
  align-items: center;
  justify-items: center;

  border-radius: 16px;
  background: var(--backgroundA-2);
`
